import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useTheme } from "@mui/material/styles";
import { Button, Typography, Tooltip, Grid } from "@mui/material";

import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import AddEditEmployeeMutation from "./AddEditEmployeeMutation/AddEditEmployeeMutation";

import { EmployeesApiService } from "../../../services/employees/employees-api.service";
import { EmployeesCurrentMutationsApiService } from "../../../services/employees/employees-current-mutations-api.service";

import { MutationsFilter } from "../../../models/mutations/mutations-filter";
import { MUTATIONS_STATUSES } from "../../../utils/constants/constants";
import { MutationStatusRef } from "../../../models/mutation-statuses/mutation-status-ref";
import { EmployeeMutationSTO } from "../../../models/employees/mutations/employee-mutation-sto";
import { EmployeeMutationDTO } from "../../../models/employees/mutations/employee-mutation-dto";

import useAbortableLoadResponse from "../../../custom-hooks/useAbortableLoadResponse/useAbortableLoadResponse";
import { intlFormatDateShort } from "../../../utils/time-utils";

import { EmployeeProps, State } from "./interfaces";
import { Container, EmployeeContainer } from "./styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBirthdayCake, faX } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderPortal from "../HeaderPortal";
const EmployeeSelectionAndMutation = ({ employee, handleChangeEmployee, documentsOrData }: EmployeeProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const tRP = "components.data_and_documents.my_data_and_documents"; // tRP - translation root path

  const [state, setState] = useState<State>({
    mutation: null,
    hasMutation: false,
  });

  const { response } = useAbortableLoadResponse<EmployeeMutationDTO>(EmployeesCurrentMutationsApiService, {
    propsFilter: { employeeId: employee?.id } as MutationsFilter,
  });

  const getEmployeeMutations = useCallback(() => {
    const newEmployeeMutation: EmployeeMutationSTO = {
      isDeleted: false,
      id: uuidv4(),
      companyAccount: null,
      legalEntity: employee?.legalEntity ?? null,
      employee,
      firstName: employee?.firstName ?? "",
      lastName: employee?.lastName ?? "",
      employeeCode: employee?.employeeCode ?? "",
      taxId: "",
      status: {
        id: MUTATIONS_STATUSES.PENDING.id,
      } as MutationStatusRef,
      birthDate: employee?.birthDate ?? null,
    };

    setState(() => ({
      hasMutation: !!response?.items?.length,
      mutation: response?.items[0] ?? newEmployeeMutation,
    }));
  }, [employee, response?.items]);

  useEffect(() => {
    if (response?.id) getEmployeeMutations();
  }, [response?.id, getEmployeeMutations]);

  const { mutation, hasMutation } = state;

  return (
    // <Container>
    <>
      <HeaderPortal title={documentsOrData === "data" ? t(`${tRP}.title_data`) : t(`${tRP}.title_documents`)} />
      <Grid
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          position: "sticky",
          top: "0",
          backgroundColor: theme.palette.background.default,
          padding: "10px 0px",
          zIndex: 10,
        }}
      >
        <EmployeeContainer>
          <AutocompleteDropdown
            field="employee"
            childField="legalEntity"
            childFieldProp="name"
            serviceApi={new EmployeesApiService()}
            apiLoadItemsPath="/GetCurrentUserEmployees"
            // labelKey={`${tRP}.employee.title`}
            labelKey={"employee"}
            handleChange={(_, value) => handleChangeEmployee(value)}
            inputVariant="standard"
            selectedValue={employee}
            inputStyle={{
              width: "330px",
              backgroundColor: theme.palette.background.paper,
              borderRadius: "5px 0px 0px 0px",
              padding: "6px 40px 0px 10px",
            }}
            quickFilterLike={true}
          />
          {employee && (
            <>
              <Tooltip title={`${t(`${tRP}.employee.fields.birthDate`)}`}>
                <Typography>
                  <FontAwesomeIcon icon={faBirthdayCake} />
                  {employee && employee.birthDate ? intlFormatDateShort(employee.birthDate) : ""}
                </Typography>
              </Tooltip>
              <Tooltip title={`${t(`${tRP}.employee.fields.taxId`)}: ${employee?.taxId}`}>
                <Typography>{employee?.taxId}</Typography>
              </Tooltip>
            </>
          )}
          {employee && (
            <AddEditEmployeeMutation
              original={employee}
              mutation={mutation}
              hasMutation={hasMutation}
              notifyParent={() => handleChangeEmployee(employee)}
            />
          )}
        </EmployeeContainer>
        <Button
          className="close"
          onClick={() => navigate(-1)}
          style={{ color: theme.palette.error.main }}
        >
          <FontAwesomeIcon icon={faX} />
        </Button>
      </Grid>
    </>
    // </Container>
  );
};

export default EmployeeSelectionAndMutation;
